import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

interface KeyboardShortcutsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const shortcuts = [
  { keys: ['G', 'D'], description: 'Go to Dashboard', action: '/dashboard' },
  { keys: ['G', 'C'], description: 'Go to Chat', action: '/chat' },
  { keys: ['G', 'M'], description: 'Go to Markets', action: '/markets' },
  { keys: ['G', 'P'], description: 'Go to Portfolio', action: '/portfolio' },
  { keys: ['G', 'T'], description: 'Go to Trades', action: '/trades' },
  { keys: ['G', 'S'], description: 'Go to Settings', action: '/settings' },
  { keys: ['?'], description: 'Show keyboard shortcuts', action: 'help' },
  { keys: ['Esc'], description: 'Close modals / dialogs', action: 'escape' },
];

export function KeyboardShortcuts({ open, onOpenChange }: KeyboardShortcutsProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {shortcuts.map((shortcut, i) => (
            <div 
              key={i} 
              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
            >
              <span className="text-sm text-muted-foreground">{shortcut.description}</span>
              <div className="flex gap-1">
                {shortcut.keys.map((key, j) => (
                  <Badge key={j} variant="outline" className="font-mono text-xs">
                    {key}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center mt-4">
          Press <Badge variant="outline" className="font-mono text-xs mx-1">?</Badge> anytime to show this dialog
        </p>
      </DialogContent>
    </Dialog>
  );
}

export function useKeyboardShortcuts(onShowHelp: () => void) {
  const navigate = useNavigate();

  useEffect(() => {
    let keySequence: string[] = [];
    let lastKeyTime = 0;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      const now = Date.now();
      const key = e.key.toUpperCase();

      // Reset sequence if too much time has passed
      if (now - lastKeyTime > 500) {
        keySequence = [];
      }
      lastKeyTime = now;

      // Handle single key shortcuts
      if (key === '?') {
        e.preventDefault();
        onShowHelp();
        return;
      }

      // Build key sequence
      keySequence.push(key);

      // Check for navigation shortcuts
      if (keySequence.length >= 2) {
        const sequence = keySequence.slice(-2).join('');
        
        switch (sequence) {
          case 'GD':
            navigate('/dashboard');
            break;
          case 'GC':
            navigate('/chat');
            break;
          case 'GM':
            navigate('/markets');
            break;
          case 'GP':
            navigate('/portfolio');
            break;
          case 'GT':
            navigate('/trades');
            break;
          case 'GS':
            navigate('/settings');
            break;
        }

        // Reset after match
        keySequence = [];
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, onShowHelp]);
}
